import { memo, useState, useMemo, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../../convex/_generated/api';
import { Id } from '../../../convex/_generated/dataModel';
import { ICalendarEvent } from '@/@types/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';

interface IRescheduleDialogProps {
  event: ICalendarEvent | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * RescheduleDialog Component
 * Allows users to reschedule a scheduled post by selecting a new date and time
 *
 * Features:
 * - Calendar date picker
 * - Time input field
 * - Validation for past times
 * - Validation to prevent rescheduling published/failed posts
 */
const RescheduleDialog = ({ event, isOpen, onClose, onSuccess }: IRescheduleDialogProps) => {
  const reschedulePost = useMutation(api.socialMedia.scheduledPublishing.reschedulePost);

  // ============================================
  // 1. STATE
  // ============================================
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>('12:00');
  const [isRescheduling, setIsRescheduling] = useState(false);

  // ============================================
  // 2. COMPUTED VALUES
  // ============================================
  const canReschedule = useMemo(() => {
    return event?.status === 'scheduled';
  }, [event?.status]);

  const scheduledDateTime = useMemo(() => {
    if (!selectedDate) return null;

    const [hours, minutes] = selectedTime.split(':').map(Number);
    const dateTime = new Date(selectedDate);
    dateTime.setHours(hours, minutes, 0, 0);

    return dateTime;
  }, [selectedDate, selectedTime]);

  const isPastTime = useMemo(() => {
    if (!scheduledDateTime) return false;
    return scheduledDateTime.getTime() < Date.now();
  }, [scheduledDateTime]);

  const isValid = useMemo(() => {
    return selectedDate && !isPastTime && canReschedule;
  }, [selectedDate, isPastTime, canReschedule]);

  // ============================================
  // 3. HANDLERS
  // ============================================
  const handleReschedule = useCallback(async () => {
    if (!event || !scheduledDateTime || !isValid) return;

    setIsRescheduling(true);

    try {
      await reschedulePost({
        scheduledPostId: event.id as Id<'scheduledPosts'>,
        newScheduledFor: scheduledDateTime.getTime(),
      });

      toast.success('Post rescheduled successfully');
      onSuccess?.();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to reschedule post';
      toast.error(message);
    } finally {
      setIsRescheduling(false);
    }
  }, [event, scheduledDateTime, isValid, reschedulePost, onSuccess, onClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
        setSelectedDate(undefined);
        setSelectedTime('12:00');
      }
    },
    [onClose]
  );

  // ============================================
  // 4. INITIALIZE DATE/TIME FROM EVENT
  // ============================================
  const handleDialogOpen = useCallback(() => {
    if (event && isOpen) {
      setSelectedDate(event.scheduledFor);
      setSelectedTime(format(event.scheduledFor, 'HH:mm'));
    }
  }, [event, isOpen]);

  // Effect to initialize when dialog opens
  useMemo(() => {
    handleDialogOpen();
  }, [handleDialogOpen]);

  // ============================================
  // 5. RENDER
  // ============================================
  if (!event) return null;

  const statusColorMap: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
    published: 'bg-green-100 text-green-700 border-green-200',
    failed: 'bg-red-100 text-red-700 border-red-200',
    publishing: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
  };

  const statusColor = statusColorMap[event.status] || statusColorMap.scheduled;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{canReschedule ? 'Reschedule Post' : 'Post Details'}</DialogTitle>
        </DialogHeader>

        <div className={canReschedule ? 'space-y-6 py-4' : 'space-y-4 py-4'}>
          {/* Post Info */}
          <div className="rounded-lg border bg-muted/50 p-4">
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-medium text-sm">Post Details</h4>
              <span className={`px-2 py-1 rounded text-xs font-medium border ${statusColor}`}>
                {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-1 truncate">{event.title}</p>
            <p className="text-xs text-muted-foreground">
              {canReschedule ? 'Currently scheduled' : 'Scheduled for'}:{' '}
              {format(event.scheduledFor, 'PPP')} at {format(event.scheduledFor, 'HH:mm')}
            </p>
          </div>

          {/* Cannot reschedule warning */}
          {!canReschedule && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
              <p className="text-sm text-destructive font-medium mb-2">
                Cannot Reschedule {event.status.charAt(0).toUpperCase() + event.status.slice(1)}{' '}
                Posts
              </p>
              <p className="text-xs text-muted-foreground">
                Only posts with "Scheduled" status can be rescheduled. This post has already been{' '}
                {event.status}.
              </p>
            </div>
          )}

          {/* Date Picker */}
          {canReschedule && (
            <>
              <div className="space-y-2">
                <Label>Select Date</Label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  className="rounded-md border"
                />
              </div>

              {/* Time Picker */}
              <div className="space-y-2">
                <Label htmlFor="time">Select Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={selectedTime}
                  onChange={e => setSelectedTime(e.target.value)}
                  className="w-full"
                />
                {isPastTime && (
                  <p className="text-xs text-destructive">Selected time is in the past</p>
                )}
              </div>

              {/* Preview */}
              {scheduledDateTime && !isPastTime && (
                <div className="rounded-lg border bg-primary/5 p-3">
                  <p className="text-sm font-medium">New schedule time:</p>
                  <p className="text-sm text-muted-foreground">
                    {format(scheduledDateTime, 'PPP')} at {format(scheduledDateTime, 'HH:mm')}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isRescheduling}
          >
            Cancel
          </Button>
          {canReschedule && (
            <Button onClick={handleReschedule} disabled={!isValid || isRescheduling}>
              {isRescheduling ? 'Rescheduling...' : 'Reschedule'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default memo(RescheduleDialog);
